import { AnimatedFlame } from 'app/components/streak/animated-flame';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { PCImage } from 'app/design/primitives/image';
import { getImageVariantUrl } from 'app/lib/image-variants';
import type { MonthlyStreakMember } from 'app/network/monthly-stats';

import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { useNavigation } from 'app/hooks/navigation';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface HeroHeaderProps {
  postalCode: string;
  onBack: () => void;
  members: MonthlyStreakMember[];
  memberCount: number;
  onMembersPress: () => void;
}

const AVATAR_SIZE = 40;
const AVATAR_OFFSET = 28;

export function HeroHeader({ postalCode, onBack, members, memberCount, onMembersPress }: HeroHeaderProps) {
  const { t } = useTranslation('streak');
  const { isSmallUI } = useUISize();
  const navigation = useNavigation();
  const parentState = navigation.getParent()?.getState();
  const isInProfile = parentState?.routes[parentState.index]?.name === 'profile';
  const membersWithPictures = members.filter((member) => !!member.profile?.picture).slice(0, 3);
  return (
    <Column gap="spacing.general.x2" paddingTop={isInProfile && !isSmallUI ? 'spacing.general.x8' : undefined}>
      <Row alignItems="flex-start" justifyContent="space-between" gap="spacing.general.x2">
        {!isInProfile || isSmallUI ? (
          <Pressable hitSlop={10} onPress={onBack}>
            <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={32} color={theme.colors['text.primary']} />
          </Pressable>
        ) : null}
        <Column flex={1} gap="spacing.general.x1">
          <Text fontSize={22} fontWeight="bold">
            {t('title')}
          </Text>
          <Text color="text.primary" fontSize={14}>
            {postalCode}
          </Text>
          {memberCount > 0 ? (
            <Pressable
              accessibilityLabel={t('membersCount', { count: memberCount, displayCount: memberCount })}
              accessibilityRole="button"
              hitSlop={8}
              onPress={onMembersPress}
            >
              <Row alignItems="center" gap="spacing.general.x2">
                {membersWithPictures.length > 0 ? (
                  <Row height={AVATAR_SIZE} width={AVATAR_SIZE + (membersWithPictures.length - 1) * AVATAR_OFFSET}>
                    {membersWithPictures.map((member, index) =>
                      member.profile?.picture ? (
                        <PCImage
                          key={member.id}
                          aspectRatio={1}
                          borderColor="white"
                          borderWidth={2}
                          cachePolicy="memory-disk"
                          height={AVATAR_SIZE}
                          recyclingKey={`${member.id}-streak-avatar`}
                          source={{ uri: getImageVariantUrl(member.profile.picture, 'mini') }}
                          style={{
                            borderRadius: AVATAR_SIZE / 2,
                            left: index * AVATAR_OFFSET,
                            position: 'absolute',
                            zIndex: membersWithPictures.length - index,
                          }}
                          width={AVATAR_SIZE}
                        />
                      ) : null,
                    )}
                  </Row>
                ) : null}
                <Text fontSize={14} fontWeight="600">
                  {t('membersCount', {
                    count: memberCount,
                    displayCount: memberCount > 99 ? '99+' : memberCount,
                  })}
                </Text>
              </Row>
            </Pressable>
          ) : null}
        </Column>

        <AnimatedFlame size={56} />
      </Row>
    </Column>
  );
}
